<template>
  <header class="bg-surface/45 dark:bg-[#050B18]/75 backdrop-blur-xl border-b border-border theme-transition sticky top-0 z-40">
    <div class="px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <!-- Left side -->
        <div class="flex items-center gap-4">
          <!-- Mobile menu button -->
          <button
            @click="$emit('toggle-sidebar')"
            class="lg:hidden p-2 rounded-xl text-text-muted hover:text-text-primary hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-[#A6FF00]"
          >
            <Bars3Icon class="h-6 w-6" />
          </button>
          
          <!-- Search bar -->
          <div class="hidden sm:block">
            <div class="relative w-64 lg:w-80">
              <div class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                <MagnifyingGlassIcon class="h-4.5 w-4.5 text-text-muted" />
              </div>
              <input
                v-model="searchQuery"
                @input="handleSearch"
                type="text"
                placeholder="Global Search..."
                class="block w-full pl-10 pr-4 py-2 border border-border rounded-xl bg-surface/50 text-text-primary placeholder-text-muted focus:outline-none focus:border-[#A6FF00] focus:ring-2 focus:ring-[#A6FF00]/15 sm:text-sm theme-transition"
              />
            </div>
          </div>
        </div>

        <!-- Right side -->
        <div class="flex items-center space-x-4">
          <!-- Live System Indicator -->
          <div class="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-[#00C853]/10 border border-[#00C853]/20 rounded-xl">
            <span class="w-1.5 h-1.5 rounded-full bg-[#00C853] animate-pulse"></span>
            <span class="text-[9px] font-black uppercase tracking-wider text-[#00C853]">System Active</span>
          </div>

          <!-- Real-Time Clock -->
          <div class="hidden md:flex flex-col items-end px-3 py-0.5 bg-surface/50 border border-border rounded-xl tabular-nums">
            <span class="text-xs font-black text-text-primary leading-tight">{{ currentTime }}</span>
            <span class="text-[8px] text-text-muted font-black uppercase tracking-widest">local time</span>
          </div>

          

         

          <!-- Notifications -->
          <div class="relative">
            <button
              @click="notificationsOpen = !notificationsOpen"
              class="p-2 rounded-xl text-text-secondary hover:bg-surface-hover hover:text-[#A6FF00] focus:outline-none theme-transition"
              aria-label="notifications"
            >
              <BellIcon class="h-6 w-6" />
              <span 
                v-if="unreadNotifications > 0"
                class="absolute top-1.5 right-1.5 inline-flex items-center justify-center w-2 h-2 bg-danger rounded-full"
              ></span>
            </button>
            
            <!-- Notifications dropdown -->
            <div
              v-if="notificationsOpen"
              class="absolute right-0 mt-2 w-80 bg-[#050B18]/95 backdrop-blur-2xl rounded-xl shadow-2xl border border-border/60 z-50 notifications-dropdown"
            >
              <div class="py-2">
                <div class="px-4 py-2 border-b border-border/50">
                  <h3 class="text-xs font-black text-text-primary uppercase tracking-wider">Notifications</h3>
                </div>
                <div class="max-h-96 overflow-y-auto scrollbar-custom">
                  <div v-if="notifications.length === 0" class="px-4 py-8 text-center text-text-muted">
                    <BellSlashIcon class="mx-auto h-10 w-10 opacity-30" />
                    <h3 class="mt-2 text-xs font-bold">No notifications</h3>
                  </div>
                  <div v-else>
                    <div
                      v-for="notification in notifications"
                      :key="notification.id"
                      class="px-4 py-3 hover:bg-surface-hover cursor-pointer border-b border-border/30 last:border-0"
                      @click="markAsRead(notification.id)"
                    >
                      <div class="flex items-start">
                        <div class="flex-shrink-0 mt-0.5">
                          <div 
                            class="w-2.5 h-2.5 rounded-full"
                            :class="notification.status === 'unread' ? 'bg-[#A6FF00] shadow-[0_0_8px_rgba(166,255,0,0.6)]' : 'bg-border/60'"
                          ></div>
                        </div>
                        <div class="ml-3 flex-1">
                          <p class="text-xs font-bold text-text-primary">
                            {{ notification.message }}
                          </p>
                          <p class="text-[10px] text-text-muted mt-1 font-medium">
                            {{ formatTime(notification.createdAt) }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="px-4 py-2 border-t border-border/50 text-center">
                  <router-link
                    to="/notifications"
                    class="text-xs text-[#A6FF00] font-black hover:underline"
                  >
                    View all notifications
                  </router-link>
                </div>
              </div>
            </div>
          </div>

          <!-- User menu -->
          <div class="relative">
            <button
              @click="userMenuOpen = !userMenuOpen"
              class="flex items-center space-x-3 p-1 rounded-xl text-text-secondary hover:bg-surface-hover focus:outline-none theme-transition"
              aria-label="user-menu"
            >
              <div class="flex-shrink-0">
                <div class="w-8 h-8 bg-gradient-to-br from-[#A6FF00] to-[#00C853] rounded-xl flex items-center justify-center border border-border overflow-hidden">
                  <img v-if="userProfileImage && !imageError" :src="userProfileImage" alt="User Profile" class="w-full h-full object-cover" @error="imageError = true" />
                  <span v-else class="text-xs font-black text-[#050B18]">
                    {{ userInitials }}
                  </span>
                </div>
              </div>
              <div class="hidden md:block text-left">
                <p class="text-xs font-black text-text-primary">{{ authStore.userName || 'User' }}</p>
                <p class="text-[9px] text-text-muted uppercase tracking-widest font-black">{{ authStore.userRole || 'user' }}</p>
              </div>
              <ChevronDownIcon class="h-4 w-4 text-text-muted" />
            </button>
            
            <!-- User dropdown -->
            <div
              v-if="userMenuOpen"
              class="absolute right-0 mt-2 w-48 bg-[#050B18]/95 backdrop-blur-2xl rounded-xl shadow-2xl border border-border/60 z-50 user-menu-dropdown"
              >
              <div class="py-1">
                <router-link
                  to="/app/profile"
                  class="flex items-center px-4 py-2.5 text-xs font-bold text-text-primary hover:bg-[#A6FF00]/10 hover:text-[#A6FF00]"
                  @click="userMenuOpen = false"
                >
                  <UserIcon class="mr-3 h-4 w-4 text-text-muted" />
                  Profile
                </router-link>
                
                <div class="border-t border-border/50 mt-1 pt-1"></div>
                <button
                  @click="handleLogout"
                  class="flex items-center w-full px-4 py-2.5 text-xs font-bold text-danger hover:bg-danger/5"
                >
                  <ArrowRightOnRectangleIcon class="mr-3 h-4 w-4 opacity-70" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../../store/auth'
import { useThemeStore } from '../../store/theme'
import { useToast } from 'vue-toastification'
import {
  Bars3Icon,
  MagnifyingGlassIcon,
  BellIcon,
  BellSlashIcon,
  UserIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  SunIcon,
  MoonIcon,
  PlusIcon,
  DocumentChartBarIcon
} from '@heroicons/vue/24/outline'

// Props
const props = defineProps({
  searchPlaceholder: {
    type: String,
    default: 'Search...'
  }
})

// Emits
const emit = defineEmits(['toggle-sidebar', 'logout', 'search'])

// Composables
const router = useRouter()
const authStore = useAuthStore()
const themeStore = useThemeStore()
const toast = useToast()

// State
const searchQuery = ref('')
const notificationsOpen = ref(false)
const userMenuOpen = ref(false)
const quickActionsOpen = ref(false)
const notifications = ref([])
const unreadNotifications = ref(0)
const currentTime = ref('')
const imageError = ref(false)

const quickActions = [
  { label: 'New Transaction', to: '/app/transactions/new', icon: PlusIcon },
  { label: 'Generate Report', to: '/app/reports', icon: DocumentChartBarIcon },
  { label: 'System Settings', to: '/app/settings', icon: Cog6ToothIcon }
]

// Computed
const userInitials = computed(() => {
  if (!authStore.user) return 'U'
  
  const firstName = authStore.user.firstName || ''
  const lastName = authStore.user.lastName || ''
  
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  } else if (firstName) {
    return firstName.charAt(0).toUpperCase()
  } else if (lastName) {
    return lastName.charAt(0).toUpperCase()
  } else if (authStore.user.email) {
    return authStore.user.email.charAt(0).toUpperCase()
  } else {
    return 'U'
  }
})

const userProfileImage = computed(() => {
  if (!authStore.user || !authStore.user.profileImage) return null
  const url = authStore.user.profileImage
  if (url.startsWith('http')) return url
  const baseUrl = import.meta.env.VITE_IMAGE_URL || 'https://api.dpark.lk'
  const separator = url.startsWith('/') ? '' : '/'
  return `${baseUrl}${separator}${url}`
})

// Methods
const handleSearch = () => {
  emit('search', searchQuery.value)
}

const handleLogout = async () => {
  try {
    await authStore.logout()
    router.push('/login')
    toast.success('Logged out successfully')
  } catch (error) {
    toast.error('Logout failed')
  }
}

const markAsRead = async (notificationId) => {
  try {
    // Mark notification as read
    const notification = notifications.value.find(n => n.id === notificationId)
    if (notification && notification.status === 'unread') {
      notification.status = 'read'
      unreadNotifications.value--
    }
  } catch (error) {
    console.error('Error marking notification as read:', error)
  }
}

const formatTime = (timestamp) => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffInMinutes = Math.floor((now - date) / (1000 * 60))
  
  if (diffInMinutes < 1) return 'Just now'
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`
  return date.toLocaleDateString()
}

const fetchNotifications = async () => {
  try {
    // Mock notifications for now
    notifications.value = [
      {
        id: 1,
        message: 'New transaction completed',
        status: 'unread',
        createdAt: new Date(Date.now() - 5 * 60 * 1000)
      },
      {
        id: 2,
        message: 'System maintenance scheduled',
        status: 'read',
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000)
      }
    ]
    
    unreadNotifications.value = notifications.value.filter(n => n.status === 'unread').length
  } catch (error) {
    console.error('Error fetching notifications:', error)
  }
}

// Click outside handlers
const handleClickOutside = (event) => {
  const notificationsDropdown = event.target.closest('.notifications-dropdown')
  const userMenuDropdown = event.target.closest('.user-menu-dropdown')
  const quickActionsDropdown = event.target.closest('.quick-actions-dropdown')
  const notificationsButton = event.target.closest('button[aria-label="notifications"]')
  const userMenuButton = event.target.closest('button[aria-label="user-menu"]')
  const quickActionsButton = event.target.closest('.quick-actions-dropdown button')
  
  if (!notificationsDropdown && !notificationsButton) {
    notificationsOpen.value = false
  }
  if (!userMenuDropdown && !userMenuButton) {
    userMenuOpen.value = false
  }
  if (!quickActionsDropdown && !quickActionsButton) {
    quickActionsOpen.value = false
  }
}

const updateTime = () => {
  currentTime.value = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  }).format(new Date())
}

let timer = null

// Lifecycle
onMounted(() => {
  fetchNotifications()
  updateTime()
  timer = setInterval(updateTime, 1000)
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* Smooth transitions */
.transition-all {
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.notifications-dropdown,
.user-menu-dropdown,
.quick-actions-dropdown > div {
  animation: scale-in 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes scale-in {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-6px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Hover effects */
.hover\:bg-gray-100:hover {
  transition: background-color 0.2s ease;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .hidden {
    display: none;
  }
}
</style>
